import React from 'react';
import { Avatar, Card, CardActionArea, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useNavigate } from 'react-router-dom';

import { categorySummary } from 'app/shared/devices/deviceDisplay';

// Tarjeta de local. Habla el mismo idioma que DeviceCard: borde, hover de puro CSS y una fila de íconos con conteos,
// sin animaciones que compitan con el contenido.
const StoreItem = ({ store, onEditClick, onDeleteClick, onExportClick, onShiftsClick }) => {
    const navigate = useNavigate();
    const [actionsAnchor, setActionsAnchor] = React.useState(null);
    const canRename = typeof onEditClick === 'function';
    const canDelete = typeof onDeleteClick === 'function';
    const canExport = typeof onExportClick === 'function';
    const canEditShifts = typeof onShiftsClick === 'function';
    const hasActionsMenu = canRename || canDelete || canExport || canEditShifts;
    const devicesQty = store?.stats?.devices_qty || 0;
    const silentQty = store?.stats?.silent_qty || 0;
    const categories = categorySummary(store?.stats?.categories);

    const runAction = (action) => () => {
        setActionsAnchor(null);
        action(store);
    };

    return (
        <Card
            variant="outlined"
            sx={{
                height: "100%",
                borderRadius: 2.5,
                transition: "border-color 150ms ease, box-shadow 150ms ease",
                "&:hover": { borderColor: "primary.main", boxShadow: "0 8px 20px rgba(15, 23, 42, 0.07)" },
            }}
        >
            <CardActionArea
                component="div"
                onClick={() => { navigate(`/automations/stores/${store.id}`); }}
                sx={{ height: "100%", p: 2, alignItems: "stretch" }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ width: 40, height: 40, flexShrink: 0, bgcolor: 'primary.main' }}>
                        <StorefrontIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h4" noWrap sx={{ minWidth: 0, flex: 1, mb: 0, fontWeight: 600 }}>
                        {store?.name || 'Local sin nombre'}
                    </Typography>
                    {hasActionsMenu && (
                        <Tooltip title="Acciones">
                            <IconButton
                                size="small"
                                aria-label="Acciones del local"
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setActionsAnchor(e.currentTarget); }}
                                sx={{ flexShrink: 0 }}
                            >
                                <MoreVertIcon color="energy_off" fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flexWrap: "wrap" }} useFlexGap>
                        <Typography variant="caption" color="text.secondary">
                            {devicesQty > 0
                                ? `${devicesQty} ${devicesQty === 1 ? 'dispositivo' : 'dispositivos'}`
                                : 'Sin dispositivos'}
                        </Typography>
                        {categories.map((category) => (
                            <Tooltip key={`category-${category.key}`} title={category.label}>
                                <Stack direction="row" alignItems="center" spacing={0.15}>
                                    <category.icon fontSize="small" color={category.color} />
                                    <Typography variant="caption" color="text.primary">{category.quantity}</Typography>
                                </Stack>
                            </Tooltip>
                        ))}
                    </Stack>
                    {devicesQty > 0 && (
                        <Typography
                            variant="caption"
                            color={silentQty > 0 ? 'warning.main' : 'text.secondary'}
                            sx={{ flexShrink: 0 }}
                        >
                            {silentQty > 0 ? `${silentQty} sin reportar` : 'Todos reportando'}
                        </Typography>
                    )}
                </Stack>
            </CardActionArea>

            {/* El menú vive fuera del CardActionArea: los eventos del portal suben por el árbol de React, así que un
                click en su fondo abriría el local. */}
            {hasActionsMenu && (
                <Menu
                    anchorEl={actionsAnchor}
                    open={Boolean(actionsAnchor)}
                    onClose={() => setActionsAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {canExport && (
                        <MenuItem onClick={runAction(onExportClick)}>
                            <ListItemIcon>
                                <DownloadOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Descargar datos</ListItemText>
                        </MenuItem>
                    )}
                    {canEditShifts && (
                        <MenuItem onClick={runAction(onShiftsClick)}>
                            <ListItemIcon>
                                <ScheduleOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Turnos del local</ListItemText>
                        </MenuItem>
                    )}
                    {canRename && (
                        <MenuItem onClick={runAction(onEditClick)}>
                            <ListItemIcon>
                                <EditOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Renombrar local</ListItemText>
                        </MenuItem>
                    )}
                    {canDelete && (
                        <MenuItem onClick={runAction(onDeleteClick)} sx={{ color: 'error.main' }}>
                            <ListItemIcon sx={{ color: 'inherit' }}>
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Eliminar local</ListItemText>
                        </MenuItem>
                    )}
                </Menu>
            )}
        </Card>
    );
};

export default StoreItem;
